import { memo, useCallback, useEffect, useReducer, useRef } from 'react';

import { CirclePlusIcon } from 'lucide-react';
import PropTypes from 'prop-types';

import { Button } from '@/shadcn/button';
import { cn } from '@/shadcn/lib/utils';

import { handleUploadFileOnError, uploadFile } from '@/client/media';
import InfoTooltip from '@/components/ui/utils/infoTooltip';
import theme from '@/style';
import { useTooltip } from '@/utils/hooks';
import { BatchForm } from './batchForm/batchForm';
import {
  FORMS_MARGIN_RIGHT,
  FORMS_WIDTH
} from './batchForm/constants.batchForm';
import ErrorsSnackbar from './errorsSnackbar';
import {
  applyFormsDataChange,
  FORMS_BATCH_ADD,
  FORMS_BATCH_GLOBAL_VALIDATION_STATUS_SUCCESS,
  FORMS_BATCH_GLOBAL_VALIDATION_STATUS_VALIDATING,
  FORMS_BATCH_INIT_VALIDATION,
  FORMS_BATCH_MAIN_FORM_ID,
  initFormsData
} from './reducers';

const PLUS_ZONE_TOOLTIP_POSITION = {
  place: 'above',
  adjustPlace: true
};

export const FormsBatch = memo(
  ({
    workflowContext,
    schema,
    uiSchema,
    maxForms = 10,
    onSubmit,
    submitDisabled = false,
    onCancel,
    cachedUpdatesKey = null
  }) => {
    // forms data
    const formsDataReducer = useCallback(
      (currentFormsData, action) =>
        applyFormsDataChange(currentFormsData, action, cachedUpdatesKey),
      [cachedUpdatesKey]
    );
    const [
      { mainFormData, locks, additionalForms, globalValidationStatus, errors },
      updateForms
    ] = useReducer(formsDataReducer, { cachedUpdatesKey }, initFormsData); // use lazy initialiser to avoid running this function at each rerender

    // callback to upload files
    const handleUploadFile = useCallback(
      (file, onSuccess, onError, onProgress, disableEncryption) => {
        // upload the file
        uploadFile(
          file,
          onSuccess,
          err => {
            handleUploadFileOnError(
              file.name,
              err.response && err.response.status
            );
            return onError(err);
          },
          onProgress,
          disableEncryption
        );
      },
      []
    );

    // trigger global validation
    const runValidation = useCallback(() => {
      updateForms({ type: FORMS_BATCH_INIT_VALIDATION });
    }, [updateForms]);
    useEffect(() => {
      // if the validaiton state is completed and successful, submit all the forms together
      if (
        globalValidationStatus === FORMS_BATCH_GLOBAL_VALIDATION_STATUS_SUCCESS
      ) {
        // trigger submission function
        onSubmit([{ data: mainFormData }, ...additionalForms], () => {
          if (cachedUpdatesKey) {
            localStorage.removeItem(cachedUpdatesKey);
          }
        });
      }
    }, [globalValidationStatus]);

    // if active error form id changes, scroll to it
    const activeErrorFormId = errors ? errors.activeFormId : undefined;
    const formsContainerRef = useRef(null);

    useEffect(() => {
      if (activeErrorFormId && formsContainerRef.current) {
        // find the position of the form to scroll to
        if (activeErrorFormId === FORMS_BATCH_MAIN_FORM_ID) {
          // scroll to the start of the container
          formsContainerRef.current.scrollTo({
            left: 0,
            behavior: 'smooth'
          });
        } else {
          const formIdx = additionalForms.findIndex(
            ({ id }) => id === activeErrorFormId
          );

          formsContainerRef.current.scrollTo({
            left: (FORMS_WIDTH + FORMS_MARGIN_RIGHT) * formIdx,
            behavior: 'smooth'
          });
        }
      }
    }, [errors]);

    // tooltip of the plus zone
    const plusZoneIconRef = useRef(null);
    const [onPlusZoneMouseEnter, onPlusZoneMouseLeave, showPlusZoneToolip] =
      useTooltip(true);

    const nbForms = 1 + additionalForms.length;
    const validating =
      globalValidationStatus ===
      FORMS_BATCH_GLOBAL_VALIDATION_STATUS_VALIDATING;
    return (
      <>
        <div
          className="relative flex h-full flex-row flex-nowrap items-start overflow-auto p-4"
          ref={formsContainerRef}
        >
          <BatchForm
            data={mainFormData}
            dataCy={FORMS_BATCH_MAIN_FORM_ID}
            hasErrorFocus={activeErrorFormId === FORMS_BATCH_MAIN_FORM_ID}
            id={FORMS_BATCH_MAIN_FORM_ID}
            isInBatch={nbForms > 1}
            isMainForm
            lockedPaths={locks}
            schema={schema}
            uiSchema={uiSchema}
            updateForms={updateForms}
            uploadFile={handleUploadFile}
            validating={validating}
            workflowContext={workflowContext}
          />
          {additionalForms.map(({ id, data, links }) => (
            <BatchForm
              data={data}
              dataCy="additional-form"
              hasErrorFocus={activeErrorFormId === id}
              id={id}
              isInBatch
              key={id}
              linkedPaths={links}
              lockedPaths={locks}
              schema={schema}
              uiSchema={uiSchema}
              updateForms={updateForms}
              uploadFile={handleUploadFile}
              validating={validating}
              workflowContext={workflowContext}
            />
          ))}
          <div
            className={cn(
              'sticky top-0 mr-5 flex h-[150px] w-[350px] shrink-0 cursor-pointer items-center justify-center rounded border-2 border-muted-foreground border-dashed text-muted-foreground',
              nbForms === maxForms
                ? 'cursor-not-allowed'
                : 'hover:border-foreground hover:text-foreground'
            )}
            data-cy="plus-zone"
            data-is-disabled={nbForms === maxForms}
            onClick={() =>
              nbForms < maxForms && updateForms({ type: FORMS_BATCH_ADD })
            }
            onMouseEnter={onPlusZoneMouseEnter}
            onMouseLeave={onPlusZoneMouseLeave}
          >
            <div ref={plusZoneIconRef}>
              <CirclePlusIcon className="size-8" />
            </div>
          </div>
        </div>
        <div className="z-5 flex w-full shrink-0 flex-row flex-nowrap items-end justify-between border-border border-t bg-card px-4 py-3 text-sm shadow-sm">
          <Button dataCy="cancel" onClick={onCancel} variant="outline">
            CANCEL
          </Button>
          <Button
            data-cy="submit-button"
            disabled={submitDisabled}
            onClick={runValidation}
            variant="default"
          >
            {`SUBMIT TRACE${nbForms > 1 ? 'S' : ''}`}
          </Button>
        </div>
        {showPlusZoneToolip && (
          <InfoTooltip
            backgroundColor={
              nbForms < maxForms ? theme.grey2 : theme.warningRed
            }
            clientRef={plusZoneIconRef}
            delay={100}
            position={PLUS_ZONE_TOOLTIP_POSITION}
            text={
              nbForms < maxForms
                ? 'Add another trace'
                : `You have reached the maximum number of forms for a single batch ( ${maxForms} )`
            }
            textColor={theme.white1}
          />
        )}
        {errors && <ErrorsSnackbar errors={errors} updateForms={updateForms} />}
      </>
    );
  }
);
FormsBatch.propTypes = {
  workflowContext: PropTypes.object.isRequired,
  schema: PropTypes.object.isRequired,
  uiSchema: PropTypes.object.isRequired,
  maxForms: PropTypes.number,
  onSubmit: PropTypes.func.isRequired,
  submitDisabled: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  cachedUpdatesKey: PropTypes.string
};
